import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnInit, Renderer2, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { TabMenuModule } from 'primeng/tabmenu';
import { debounceTime, forkJoin, take, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MenuItem } from 'primeng/api';
import { toPng } from 'html-to-image';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { subDays } from "date-fns";

import { Driver } from '@commons/models/domain/Driver';
import { FilterControlComponent } from '@shared/components/vehicle-list/filter-control/filter-control.component';
import { NewDataStoreService } from '@shared/services/new-data-store.service';
import { FilterVehicleType } from '@src/app/enum/filters-vehicle.enum';
import { IFilterVehicle, IFilterVehicleGroup, IFilterVehicleOption, ISubmitFilters } from '@src/app/interfaces/filters-vehicle.interface';
import { FilesDddService } from '@src/app/services/api/files-ddd.service';
import { DatePickerComponent } from '@src/app/standalone-components/date-picker/date-picker.component';
import { formatDate } from '@src/app/utils/date';
import { IGetTachographDataRequest, ITachographDayData } from '@src/app/interfaces/tachograph.interface';
import { Destroyable } from '@src/app/utils/Destroyable';
import { TachographDayListComponent } from './tachograph-day/tachograph-day-list.component';
import { Vehicle } from '@commons/models/domain/Vehicle';
import { GenerateXlsFileReport } from '@src/app/utils/reports/xls-report';
import { XLSTachoRowBuilder } from '@src/app/utils/reports/xls-row-tacho-chart';
import { TachographEmptyStateComponent } from './empty-state/tachograph-empty-state.component';
import { IPdfDataObject } from '@src/app/interfaces/pdf.interface';
import { PdfGenerator } from '@src/app/utils/pdf-generator/pdf-generator';
import { endOfDay, startOfDay } from 'date-fns';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { IOrganizationVehicleUnit } from '@src/app/interfaces/api/organization-units.interface';
import { UserOrganizationsUnitsService } from '@src/app/services/api/user-organizations-units.service';

enum View {
  Vehicles = 1,
  Drivers = 2,
}

@Component({
  selector: 'app-tachograph-charts',
  standalone: true,
  imports: [TranslateModule, TabMenuModule, TooltipModule, FilterControlComponent, TachographEmptyStateComponent, SkeletonModule, DatePickerComponent, FormsModule, ButtonModule, TachographDayListComponent, CommonModule],
  templateUrl: './tachograph-charts.component.html',
  styleUrls: ['./tachograph-charts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TachographChartsComponent extends Destroyable implements OnInit, AfterViewInit {
  @ViewChild('containerList') private containerListRef: ElementRef<HTMLDivElement>;

  public readonly VIEW = View;
  public readonly TABS: MenuItem[] = [
    {label: this.translate.instant('vehicles'), command: () => this.onChangeView(View.Vehicles) },
    {label: this.translate.instant('drivers'), command: () => this.onChangeView(View.Drivers) },
  ];
  public activeTabOption = signal<MenuItem>(null!);
  public activeView = signal<View>(null!);
  public listHeight: string;

  public drivers = signal<Driver[]>([]);
  public filtersDriversConfig: WritableSignal<IFilterVehicle | null> = signal<IFilterVehicle | null>(null);
  public filtersDriversValue = signal<ISubmitFilters<number>>(null!); // id kierowcy
  public selectedDate = signal<Date[]>(null!);

  public vehicles = signal<Vehicle[]>([]);
  public filtersVehiclesConfig: WritableSignal<IFilterVehicle | null> = signal<IFilterVehicle | null>(null);
  public filtersVehiclesValue = signal<ISubmitFilters<number>>(null!); // id pojazdu
  public selectedVehicle: Vehicle; // dane zaznaczonego pojazdu w filtrach

  public tachographData = signal<ITachographDayData[]>([]);
  public isLoadingData = signal<boolean>(false);

  constructor(
    private newDataStoreService: NewDataStoreService,
    private translate: TranslateService,
    private filesDddService: FilesDddService,
    private rendere2: Renderer2,
    private vehicleService: VehicleService,
    private userOrganizationsUnitsService: UserOrganizationsUnitsService,
  ) {
    super();
    this.activeView.set(View.Vehicles);
    this.activeTabOption.set(this.TABS[0]);
  }

  ngOnInit(): void {
    this.drivers.set(this.newDataStoreService.getDrivers());
    this.vehicles.set(this.newDataStoreService.getVehicles());
    this.setFiltersConfig();
    this.initalFiltersValues();
  }

  ngAfterViewInit(): void {
    this.setListHeight();
  }

  private initalFiltersValues(): void {
    if (this.vehicles().length) {
      // const firstVehicleId = this.vehicles()[0].Id;
      // this.filtersVehiclesValue.set({ selectedControlValue: firstVehicleId });
      // this.getVehicleDataById(firstVehicleId);
      const thirtyDaysAgo = subDays(new Date(), 29);
      this.selectedDate.set([thirtyDaysAgo, new Date()]);
    }
  }

  private onChangeView(view: View): void {
    this.tachographData.set([]);
    this.filtersVehiclesValue.set(null!);
    this.filtersDriversValue.set(null!);
    this.activeView.set(view);
  }

  private setFiltersConfig(): void {
    this.filtersDriversConfig.set({
      filterLabel: this.translate.instant('driver'),
      type: FilterVehicleType.Group,
      dropdownLabel: this.translate.instant('selectDriver'),
      options: this.drivers().map(item => ({ label: `${ item.FN } ${ item.LN }`, value: item.Id, data: item })) as IFilterVehicleOption[],
      isVisibleSelectedOptionLabel: true,
    });

    this.filtersVehiclesConfig.set({
      filterLabel: this.translate.instant('labels.vehicle'),
      type: FilterVehicleType.Group,
      dropdownLabel: this.translate.instant('selectVehicle'),
      options: [],
      isVisibleSelectedOptionLabel: true,
      panelWidthPx: 400,
    });
    this.getVehiclesList();
  }

  private getVehiclesList(): void {
    const requests = {
      units: this.userOrganizationsUnitsService.GetOrganizationalUnits(),
    };

    forkJoin(requests)
    .pipe(
      takeUntil(this.destroy$),
    )
    .subscribe(
      ({ units }) => {
        this.setVehicleList(this.vehicles(), units);
      }
    );
  }

  private setVehicleList(vehicles: Vehicle[], units: IOrganizationVehicleUnit[]): void {
    const { list } = this.vehicleService.GetVehiclesGroupedV2(vehicles, units);

    this.filtersVehiclesConfig.set({
      ...this.filtersVehiclesConfig() as IFilterVehicle,
      options: list as IFilterVehicleGroup[],
    });
  }

  public onChangeDriver(data: ISubmitFilters): void {
    this.filtersDriversValue.set(data);
  }

  public onChangeVehicle(data: ISubmitFilters): void {
    this.filtersVehiclesValue.set(data);
    this.getVehicleDataById(data.selectedControlValue);
  }

  private getVehicleDataById(idVehicle: number): void {
    this.vehicleService.getDetails$(idVehicle).pipe(take(1)).subscribe({
      next: (data) => {
        this.selectedVehicle = data;
      }
    });
  }

  public submitFilters(): void {
    this.isLoadingData.set(true);
    const date = new Date();
    const offsetInMinutes = date.getTimezoneOffset();

    const [startDate, endDate] = this.selectedDate();
    const body: IGetTachographDataRequest = {
      DateFrom: startOfDay(startDate).toISOString(),
      DateTo: endOfDay(endDate).toISOString(),
      UtcOffsetMinutes: Math.abs(offsetInMinutes),
      ...(this.activeView() === View.Vehicles ? { VehicleId: this.filtersVehiclesValue().selectedControlValue } : { WorkerId: this.filtersDriversValue().selectedControlValue }),
    };

    this.filesDddService.GetTachographData(body).pipe(
      takeUntil(this.destroy$),
      debounceTime(400),
    )
    .subscribe({
      next: (data) => {
        this.tachographData.set(data.Result);
        this.isLoadingData.set(false);
      }
    });
  }

  private get selectedDateFormated(): { DateFrom: string, DateTo: string } {
    const [startDate, endDate] = this.selectedDate();
    return {
      DateFrom: formatDate('yyyy-MM-dd', startDate),
      DateTo: formatDate('yyyy-MM-dd', endDate),
    }
  }

  private setListHeight(): void {
    this.listHeight = (this.containerListRef.nativeElement.clientHeight - 28) + 'px';
    this.rendere2.setStyle(this.containerListRef.nativeElement, 'max-height', this.listHeight);
  }

  public generateXlsFile(): void {
    const data = this.tachographData().flatMap(el => el.Segments.map(item => item));
    const fileName = `${ this.translate.instant('reportTachograph') } ${ this.selectedVehicle.Nm } ${ this.selectedDateFormated.DateFrom }-${ this.selectedDateFormated.DateTo }`;
    const generateXlsFileReport = new GenerateXlsFileReport(data, new XLSTachoRowBuilder(this.translate));
    generateXlsFileReport.generateReport(fileName);
  }

  public async generatePDFFile(): Promise<void> {
    const fileName = `${ this.translate.instant('reportTachograph') } ${ this.selectedVehicle.Nm } ${ this.selectedDateFormated.DateFrom }-${ this.selectedDateFormated.DateTo }.pdf`;
    const pdfElements: IPdfDataObject[] = [
      { index: 0, text: `${this.translate.instant('labels.vehicle')}: ${ this.selectedVehicle.Nm }`, isHeaderNewPage: true },
      { index: 1, text: `${this.translate.instant('dataChartFrom')}: ${ this.selectedDateFormated.DateFrom }-${ this.selectedDateFormated.DateTo }`, isHeaderNewPage: true },
    ];

    const charts = Array.from(document.getElementsByClassName('content__list__data'));
    for (const item of charts) {
      const chartsToBase64 = await toPng(item as HTMLElement, { skipFonts: true });

      pdfElements.push({
        index: pdfElements.length,
        image: chartsToBase64,
        newPage: (pdfElements.length % 12 === 0),
      });
    }

    const generator = new PdfGenerator(pdfElements, {
      spacing: 5,
      topMargin: 20,
      sideMargin: 20,
      fileName,
    });
    generator.generatePdf();
  }

}
